/**
 * Every icon the app draws (TMMAPP-0099, TMM-T7 1.4, visual contract §12): Lucide line icons at
 * stroke 1.5 with round caps and joins, hidden from assistive technology (the control around an
 * icon carries the name), at the size of their use: nav 18, inline 16, bar 20 / 24. A caller may
 * pass `size` or `color`; stroke colour is `currentColor`, so an icon takes its host's ink.
 * `@mui/icons-material` is not used (an eslint guard fails an import of it).
 */
import type { CSSProperties } from 'react';
import {
  Archive,
  ArrowLeft,
  ArrowUp,
  Bell,
  Calendar,
  ChartColumn,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  Clock,
  Copy,
  Download,
  FileSpreadsheet,
  FileText,
  GitBranch,
  GripVertical,
  Heart,
  History,
  House,
  Image,
  Info,
  Landmark,
  Leaf,
  Languages,
  ListChecks,
  Lock,
  Menu,
  MessageSquare,
  Mic,
  Moon,
  Paperclip,
  PenLine,
  Plus,
  Repeat,
  Rows2,
  Rows4,
  Search,
  Send,
  Settings,
  Shield,
  Sparkles,
  Store,
  Sun,
  SunMoon,
  Text,
  Trash2,
  TriangleAlert,
  Users,
  Video,
  Wallet,
  X,
  type LucideIcon,
  type LucideProps,
} from 'lucide-react';

export type IconProps = Omit<LucideProps, 'ref'>;

const ICON_SIZE = { nav: 18, inline: 16, bar: 20, barLarge: 24 } as const;

export const ICON_STROKE = 1.5;

function lineIcon(Icon: LucideIcon, defaultSize: number, displayName: string) {
  function AppIcon({ size = defaultSize, ...props }: IconProps) {
    return (
      <Icon
        size={size}
        strokeWidth={ICON_STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        focusable="false"
        {...props}
      />
    );
  }
  AppIcon.displayName = displayName;
  return AppIcon;
}

// Navigation (18).
export const VirtualAccountsIcon = lineIcon(ChartColumn, ICON_SIZE.nav, 'VirtualAccountsIcon');
export const AccountsIcon = lineIcon(Landmark, ICON_SIZE.nav, 'AccountsIcon');
export const MeetingsIcon = lineIcon(Calendar, ICON_SIZE.nav, 'MeetingsIcon');
export const AuditIcon = lineIcon(Text, ICON_SIZE.nav, 'AuditIcon');
export const SettingsIcon = lineIcon(Settings, ICON_SIZE.nav, 'SettingsIcon');
export const PayeesIcon = lineIcon(Store, ICON_SIZE.nav, 'PayeesIcon');
export const RecurringNavIcon = lineIcon(Repeat, ICON_SIZE.nav, 'RecurringNavIcon');
export const CashFlowIcon = lineIcon(ChartLine, ICON_SIZE.nav, 'CashFlowIcon');
export const AssistantNavIcon = lineIcon(MessageSquare, ICON_SIZE.nav, 'AssistantNavIcon');
// The v2 destinations (TMMAPP-0150 U2, visual contract v2 §12: home, sparkle, cal, users, wallet).
export const HomeNavIcon = lineIcon(House, ICON_SIZE.nav, 'HomeNavIcon');
export const AssistantSparkleNavIcon = lineIcon(Sparkles, ICON_SIZE.nav, 'AssistantSparkleNavIcon');
export const CommunityNavIcon = lineIcon(Users, ICON_SIZE.nav, 'CommunityNavIcon');
export const ToolsNavIcon = lineIcon(Wallet, ICON_SIZE.nav, 'ToolsNavIcon');
export const HelpNavIcon = lineIcon(CircleHelp, ICON_SIZE.nav, 'HelpNavIcon');

// App bar (24).
export const MenuIcon = lineIcon(Menu, ICON_SIZE.barLarge, 'MenuIcon');

// Inline (16).
export const AddIcon = lineIcon(Plus, ICON_SIZE.inline, 'AddIcon');
export const ArchiveIcon = lineIcon(Archive, ICON_SIZE.inline, 'ArchiveIcon');
export const EditIcon = lineIcon(PenLine, ICON_SIZE.inline, 'EditIcon');
export const DirectionIcon = lineIcon(ArrowUp, ICON_SIZE.inline, 'DirectionIcon');
export const DeleteIcon = lineIcon(Trash2, ICON_SIZE.inline, 'DeleteIcon');
export const CloseIcon = lineIcon(X, ICON_SIZE.bar, 'CloseIcon');
export const RecurringIcon = lineIcon(Repeat, ICON_SIZE.inline, 'RecurringIcon');
export const ExpandIcon = lineIcon(ChevronDown, ICON_SIZE.bar, 'ExpandIcon');
export const CollapseIcon = lineIcon(ChevronUp, ICON_SIZE.bar, 'CollapseIcon');
export const PreviousIcon = lineIcon(ChevronLeft, ICON_SIZE.bar, 'PreviousIcon');
export const NextIcon = lineIcon(ChevronRight, ICON_SIZE.bar, 'NextIcon');
export const CheckIcon = lineIcon(Check, ICON_SIZE.inline, 'CheckIcon');
export const CopyIcon = lineIcon(Copy, ICON_SIZE.inline, 'CopyIcon');
export const AlertsIcon = lineIcon(Bell, ICON_SIZE.inline, 'AlertsIcon');
export const DragHandleIcon = lineIcon(GripVertical, ICON_SIZE.bar, 'DragHandleIcon');
export const AiIcon = lineIcon(Sparkles, ICON_SIZE.inline, 'AiIcon');
export const MicIcon = lineIcon(Mic, ICON_SIZE.bar, 'MicIcon');
export const RuleIcon = lineIcon(ListChecks, ICON_SIZE.inline, 'RuleIcon');
export const LanguageIcon = lineIcon(Languages, ICON_SIZE.bar, 'LanguageIcon');
export const LightModeIcon = lineIcon(Sun, ICON_SIZE.bar, 'LightModeIcon');
export const DarkModeIcon = lineIcon(Moon, ICON_SIZE.bar, 'DarkModeIcon');
export const SystemModeIcon = lineIcon(SunMoon, ICON_SIZE.bar, 'SystemModeIcon');
export const ComfortableDensityIcon = lineIcon(Rows2, ICON_SIZE.bar, 'ComfortableDensityIcon');
export const CompactDensityIcon = lineIcon(Rows4, ICON_SIZE.bar, 'CompactDensityIcon');
export const ClockIcon = lineIcon(Clock, ICON_SIZE.inline, 'ClockIcon');
export const SearchIcon = lineIcon(Search, ICON_SIZE.inline, 'SearchIcon');
export const LockIcon = lineIcon(Lock, ICON_SIZE.inline, 'LockIcon');
export const UsersIcon = lineIcon(Users, ICON_SIZE.inline, 'UsersIcon');
export const HistoryIcon = lineIcon(History, ICON_SIZE.inline, 'HistoryIcon');
export const HelpIcon = lineIcon(CircleHelp, ICON_SIZE.inline, 'HelpIcon');
// The assistant in v2 (TMMAPP-0153, visual contract v2 §12: branch, back, send).
export const BranchIcon = lineIcon(GitBranch, ICON_SIZE.inline, 'BranchIcon');
export const BackIcon = lineIcon(ArrowLeft, ICON_SIZE.inline, 'BackIcon');
export const SendIcon = lineIcon(Send, ICON_SIZE.nav, 'SendIcon');
// The chat header's Privacy panel button (TMMAPP-0156 item 1).
export const PrivacyIcon = lineIcon(Shield, ICON_SIZE.inline, 'PrivacyIcon');
// Home (TMMAPP-0152, visual contract v2 §12: leaf, video, heart).
export const LeafIcon = lineIcon(Leaf, ICON_SIZE.nav, 'LeafIcon');
export const VideoIcon = lineIcon(Video, ICON_SIZE.nav, 'VideoIcon');
export const HeartIcon = lineIcon(Heart, ICON_SIZE.nav, 'HeartIcon');
// Attachments (TMMAPP-0142 U5): the composer's paperclip, and a file chip's kind.
export const AttachIcon = lineIcon(Paperclip, ICON_SIZE.bar, 'AttachIcon');
export const ImageFileIcon = lineIcon(Image, ICON_SIZE.inline, 'ImageFileIcon');
export const DocumentFileIcon = lineIcon(FileText, ICON_SIZE.inline, 'DocumentFileIcon');
export const SheetFileIcon = lineIcon(FileSpreadsheet, ICON_SIZE.inline, 'SheetFileIcon');
export const DownloadIcon = lineIcon(Download, ICON_SIZE.inline, 'DownloadIcon');
/** The select caret: the theme's `MuiSelect` `IconComponent` (it passes MUI's icon class). */
export const SelectCaret = lineIcon(ChevronDown, ICON_SIZE.bar, 'SelectCaret');

// Status (16): the meaning is also in text beside the icon.
export const InfoIcon = lineIcon(Info, ICON_SIZE.inline, 'InfoIcon');
export const SuccessIcon = lineIcon(CircleCheck, ICON_SIZE.inline, 'SuccessIcon');
export const WarningIcon = lineIcon(TriangleAlert, ICON_SIZE.inline, 'WarningIcon');
export const ErrorIcon = lineIcon(CircleAlert, ICON_SIZE.inline, 'ErrorIcon');

/**
 * The kebab (visual contract §12): three filled dots, r 1.7, in `currentColor` — the prototype's
 * own drawing, not a Lucide stroke icon.
 */
export function KebabIcon({
  size = ICON_SIZE.nav,
  style,
}: {
  size?: number;
  style?: CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      focusable="false"
      style={style}
      data-icon="kebab"
    >
      <circle cx="12" cy="5" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="12" cy="19" r="1.7" />
    </svg>
  );
}
